import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { INSIGHT_VIEWS } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { useOKRGroupByViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRGroupByViewType';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import {
  generateGoalsExploreCSVData,
  generateIndividualInsightsCSVData,
} from 'screens/Team/Rabbit/Goals/AgGridTreeView/utils/generateCSVForGoals';

import {
  CollapseAllList,
  Download,
  ExpandAllList,
  ExpandAllListStroke,
  Refresh2,
} from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES, VIEW_DOWNLOAD_INSIGHT_PRIVILEGES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendClickedRefreshEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useHandleUnsavedDataCheckIn } from './useHandleUnsavedDataCheckIn';

export const useOKREllipsisValues = ({ gridRef, selectedIntent }) => {
  const sideNavSelectedCohort = useSelectedView();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  // const { viewType: groupByType } = useOKRInsights();
  const { groupByViewType } = useOKRGroupByViewType();
  const isDataExportIntent = selectedIntent?.id === 'DATA_EXPORTS' || selectedIntent === 'Data exports';
  const { viewType: OKRManageView } = useOKRManageViewType();
  const page = useViewPageName();
  const queryClient = useQueryClient();
  const toast = useToast();

  const isGroupByGoals = groupByViewType === INSIGHT_VIEWS[1].value;
  const isGroupByIndividual = groupByViewType === INSIGHT_VIEWS[0].value;

  const { handleUnsavedDataAlert } = useHandleUnsavedDataCheckIn();

  const refreshCascadedViewData = useCallback(async () => {
    sendClickedRefreshEvent({ eventProperties: { page } });
    try {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading cascaded view data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, page]);

  //   @ts-expect-error unknown type
  const employeeGoalsAccess = useSelector((state) => state.employeeGoalsAccessPrivileges) ?? {};

  const canDownloadIndividualInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_INDIVIDUAL_INSIGHTS
  );

  const canDownloadObjectiveInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_OBJECTIVE_LEVEL_INSIGHTS
  );

  const cardTreeViewOptions = useMemo(
    () => [
      {
        label: 'Refresh list',
        onClick: refreshCascadedViewData,
        Icon: Refresh2,
      },
    ],
    [refreshCascadedViewData]
  );

  const listViewOptions = useMemo(() => {
    let options = [
      {
        label: `Expand/ collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        type: 'subMenu',
        onClick: () => {},
        subOptions: [
          {
            label: `Expand all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.expandAll(),
            Icon: ExpandAllList,
          },
          {
            label: `Collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.collapseAll(),
            Icon: CollapseAllList,
          },
        ],
        Icon: ExpandAllListStroke,
      },
      {
        label: 'Refresh list',
        onClick: () => {
          handleUnsavedDataAlert(refreshCascadedViewData);
        },
        Icon: Refresh2,
      },
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => {
              if (isGroupByGoals) {
                generateGoalsExploreCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  keyResultsName,
                  moduleName,
                  objectivesName,
                  sideNavSelectedCohort,
                });
              } else {
                generateIndividualInsightsCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  sideNavSelectedCohort,
                });
              }
            },
            description: `Download selected metrics for filtered list of ${modifyString(
              moduleName,
              StringModifier.PLURAL_SENTENCE_CASE
            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
          },
          {
            label: 'Detailed Download',
            onClick: () => {
              if (isGroupByGoals) {
                generateGoalsExploreCSVData({
                  exportedRows: 'all',
                  gridRef,
                  keyResultsName,
                  moduleName,
                  objectivesName,
                  sideNavSelectedCohort,
                });
              } else {
                generateIndividualInsightsCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  sideNavSelectedCohort,
                });
              }
            },
            description: `Download all the metrics for all the ${modifyString(
              moduleName,
              StringModifier.PLURAL_SENTENCE_CASE
            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
          },
        ],
      },
    ];

    // if it's not my data then there's no restriction on downloading
    // else it depends on access privileges
    if (sideNavSelectedCohort?.id !== -12) {
      if (isGroupByIndividual && !canDownloadIndividualInsights) {
        options.splice(2, 1);
      }
      if (isGroupByGoals && !canDownloadObjectiveInsights) {
        options.splice(2, 1);
      }
    }

    if (isGroupByIndividual) {
      // remove the 1st option as it is not required for individual insights
      options.shift();
    }

    if (isDataExportIntent) {
      options = [
        {
          label: 'Download report',
          Icon: Download,
          type: 'subMenu',
          subOptions: [
            {
              label: 'Current View Download',
              onClick: () => {
                generateIndividualInsightsCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  sideNavSelectedCohort,
                  fileName: `${moduleName}_data_export_current_view_${Date.now()}.csv`,
                });
              },
              description: `Download selected metrics for filtered list of ${modifyString(
                moduleName,
                StringModifier.PLURAL_SENTENCE_CASE
              )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
            },
            {
              label: 'Detailed Download',
              onClick: () => {
                generateIndividualInsightsCSVData({
                  exportedRows: 'all',
                  gridRef,
                  sideNavSelectedCohort,
                  fileName: `${moduleName}_data_export_detailed_${Date.now()}.csv`,
                });
              },
              description: `Download all the metrics for all the ${modifyString(
                moduleName,
                StringModifier.PLURAL_SENTENCE_CASE
              )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
            },
          ],
        },
      ];
    }

    return options;
  }, [
    canDownloadIndividualInsights,
    canDownloadObjectiveInsights,
    gridRef,
    handleUnsavedDataAlert,
    isDataExportIntent,
    isGroupByGoals,
    isGroupByIndividual,
    keyResultsName,
    moduleName,
    objectivesName,
    refreshCascadedViewData,
    sideNavSelectedCohort,
  ]);

  const ellipsisMenuOptions = useMemo(() => {
    if (['tree', 'card'].includes(OKRManageView)) {
      return cardTreeViewOptions;
    }
    return listViewOptions;
  }, [cardTreeViewOptions, listViewOptions, OKRManageView]);

  return ellipsisMenuOptions;
};
